import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, fetchConfig } from '@theme/utilities';

/**
 * Property names are storage, not copy. They are written into the order and
 * read back out of the cart on the next render, so they never get translated.
 */
const TARGET = '_cc_edit_for';
const LABEL = 'Design help for';
const NOTE = 'What needs fixing';

const NOTE_DEBOUNCE = 300;

/**
 * Section ids of every cart on the page, so one request re-renders all of them.
 * Mirrors how `component-cart-items.js` builds the same list.
 *
 * @returns {string} Comma-joined section ids.
 */
function cartSections() {
  const ids = new Set();

  for (const cart of document.querySelectorAll('cart-items-component')) {
    if (cart instanceof HTMLElement && cart.dataset.sectionId) ids.add(cart.dataset.sectionId);
  }

  return Array.from(ids).join(',');
}

/**
 * Posts to the Ajax cart API.
 *
 * @param {string} url - The cart route to post to.
 * @param {Object} payload - The request body, minus the section fields.
 * @param {Object} [options] - Request options.
 * @param {boolean} [options.sections] - Whether to ask for re-rendered sections.
 * @param {AbortSignal} [options.signal] - Signal to cancel the request.
 * @returns {Promise<any>} The parsed response.
 */
async function postCart(url, payload, options = {}) {
  const { sections = true, signal } = options;
  const body = JSON.stringify(
    sections ? { ...payload, sections: cartSections(), sections_url: window.location.pathname } : payload
  );

  const response = await fetch(url, { ...fetchConfig('json', { body }), signal });

  return response.json();
}

/**
 * Tells the rest of the theme the cart moved. `cart-items-component` listens on
 * `document` and morphs itself from the sections we asked for.
 *
 * @param {any} cart - The cart returned by the Ajax API.
 */
function announce(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'cc-design-help', { source: 'cc-design-help', sections: cart.sections })
  );
}

let sweeping = false;

/**
 * Removes fee lines whose design has left the cart.
 *
 * Normally there is nothing to do: fees are added with `parent_line_key`, and
 * Shopify removes a nested line when its parent goes. This is the fallback for
 * when the nesting did not take — it is documented not to apply on stores with
 * the script editor app installed — so a fee cannot outlive its design either
 * way. Liquid does the detecting, flagging a row `data-cc-orphan` when the key
 * in the fee's `_cc_edit_for` matches no remaining line, because only the
 * server sees the whole cart.
 *
 * The guard matters: `announce` fires `cart:update`, which calls this again
 * before the morph has cleared the rows.
 */
async function sweepOrphans() {
  if (sweeping) return;

  const rows = document.querySelectorAll('[data-cc-orphan][data-key]');
  if (!rows.length) return;

  sweeping = true;

  try {
    /** @type {Record<string, number>} */
    const updates = {};
    for (const row of rows) {
      if (row instanceof HTMLElement && row.dataset.key) updates[row.dataset.key] = 0;
    }

    announce(await postCart(Theme.routes.cart_update_url, { updates }));
  } catch (error) {
    console.error(error);
  } finally {
    sweeping = false;
  }
}

/**
 * @typedef {object} DesignHelpRefs
 * @property {HTMLInputElement} toggle - The opt-in checkbox.
 * @property {HTMLTextAreaElement} [note] - The "what needs fixing" field, present once opted in.
 */

/**
 * Attaches the design edit fee to the cart line holding the design it pays for.
 *
 * Nothing is remembered here. Every cart mutation morphs this section back to
 * server HTML, so the checkbox is ticked by Liquid finding a fee line that
 * points at this one — not by anything this class holds. All three operations
 * therefore just change the cart and let the re-render tell the truth.
 *
 * @extends {Component<DesignHelpRefs>}
 */
class DesignHelp extends Component {
  requiredRefs = ['toggle'];

  /** @type {AbortController | null} */
  #noteRequest = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#noteRequest?.abort();
    this.#saveNote.cancel();
  }

  /** Adds or removes this line's fee. */
  toggle = async () => {
    const { toggle } = this.refs;

    toggle.disabled = true;

    try {
      announce(toggle.checked ? await this.#add() : await this.#remove());
    } catch (error) {
      console.error(error);
      toggle.checked = !toggle.checked;
    } finally {
      toggle.disabled = false;
    }
  };

  /** Queues a save of the note. */
  saveNote = () => this.#saveNote();

  /**
   * Adds the fee as a nested line under the design it pays for.
   *
   * `parent_line_key` is what makes it a child rather than a loose line, and
   * Shopify takes it from there: it renders the pair together through cart,
   * checkout, and the order emails, and drops the fee on its own if the design
   * is removed.
   *
   * @returns {Promise<any>} The updated cart.
   */
  #add() {
    const { variantId, lineKey } = this.dataset;

    return postCart(Theme.routes.cart_add_url, {
      items: [
        { id: Number(variantId), quantity: 1, parent_line_key: lineKey, properties: this.#properties('') },
      ],
    });
  }

  /** @returns {Promise<any>} The updated cart. */
  #remove() {
    return postCart(Theme.routes.cart_change_url, { id: this.dataset.feeKey, quantity: 0 });
  }

  /**
   * Writes the note onto the fee line.
   *
   * Sections are deliberately not requested: a morph mid-sentence would take
   * the textarea out from under the shopper. The trade is that rewriting
   * properties mints a new line key, so the row's key is refreshed from the
   * response before the next keystroke can use a stale one.
   */
  #saveNote = debounce(async () => {
    const { note } = this.refs;
    const { feeKey, lineKey } = this.dataset;
    if (!note || !feeKey) return;

    this.#noteRequest?.abort();
    this.#noteRequest = new AbortController();

    try {
      const cart = await postCart(
        Theme.routes.cart_change_url,
        { id: feeKey, properties: this.#properties(note.value) },
        { sections: false, signal: this.#noteRequest.signal }
      );

      const fee = cart.items?.find((/** @type {any} */ item) => item.properties?.[TARGET] === lineKey);
      if (fee) this.dataset.feeKey = fee.key;
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') console.error(error);
    }
  }, NOTE_DEBOUNCE);

  /**
   * The full property set for this line's fee.
   *
   * Always the full set: the Ajax API overwrites the entire properties object
   * on any request that includes one, so a partial write would drop the hidden
   * key that ties the fee to its design and strand it.
   *
   * @param {string} note - What the shopper wants fixed.
   * @returns {Record<string, string>} The properties to write.
   */
  #properties(note) {
    const { lineKey, target } = this.dataset;

    return { [LABEL]: target ?? '', [NOTE]: note, [TARGET]: lineKey ?? '' };
  }
}

if (!customElements.get('cc-design-help')) {
  customElements.define('cc-design-help', DesignHelp);
}

document.addEventListener(ThemeEvents.cartUpdate, sweepOrphans);
sweepOrphans();
